<script setup>
import { ref, computed } from "vue";
import { Primitive } from "radix-vue";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Fenix Guardian custom variants
        "cyan-outline": 
          "bg-background border border-accent text-foreground hover:border-accent/80 shadow-[0_0_15px_hsl(184_100%_54%_/_0.2)] hover:shadow-[0_0_25px_hsl(184_100%_54%_/_0.35)]",
        "titanium":
          "bg-secondary border border-border text-foreground hover:bg-muted hover:border-muted-foreground/30",
        "hero-primary":
          "bg-background border-2 border-accent text-foreground font-semibold shadow-[0_0_20px_hsl(184_100%_54%_/_0.25)] hover:shadow-[0_0_30px_hsl(184_100%_54%_/_0.4)] hover:border-accent/90",
        "hero-secondary":
          "bg-secondary/80 border border-border text-foreground hover:bg-secondary hover:border-muted-foreground/40",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-md px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const props = defineProps({
  variant: {
    type: String,
    default: "default",
  },
  size: {
    type: String,
    default: "default",
  },
  asChild: {
    type: Boolean,
    default: false,
  },
  disableRipple: {
    type: Boolean,
    default: false,
  },
  class: {
    type: String,
    default: "",
  },
});

const ripples = ref([]);
const rippleIdRef = ref(0);
const buttonRef = ref(null);

const createRipple = (event) => {
  const button = buttonRef.current || event.currentTarget; // Fallback to currentTarget if ref not yet bound or if component structure differs
  if (!button || props.disableRipple) return;

  const rect = button.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  const newRipple = {
    id: rippleIdRef.value++,
    x,
    y,
  };

  ripples.value.push(newRipple);

  // Remove ripple after animation
  setTimeout(() => {
    ripples.value = ripples.value.filter((r) => r.id !== newRipple.id);
  }, 600);
};

const handleClick = (event) => {
  createRipple(event);
};
</script>

<template>
  <Primitive
    :as="asChild ? 'slot' : 'button'"
    :as-child="asChild"
    :class="cn(buttonVariants({ variant, size }), 'relative overflow-hidden', props.class)"
    @click="handleClick"
    ref="buttonRef"
  >
    <!-- Ripple effects -->
    <span
      v-for="ripple in ripples"
      :key="ripple.id"
      class="absolute rounded-full bg-foreground/20 animate-ripple pointer-events-none"
      :style="{
        left: `${ripple.x}px`,
        top: `${ripple.y}px`,
        transform: 'translate(-50%, -50%)',
      }"
    ></span>
    <span class="relative z-10 inline-flex items-center justify-center gap-2">
      <slot />
    </span>
  </Primitive>
</template>
